
import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Tooltip,
  Divider,
  ListSubheader,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Grid,
  Button,
  keyframes,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Collapse
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import dayjs from 'dayjs';
import ReservationEditorPanel from './editor2';
import { COMMUNICATION_MEDIA_COLORS } from '../constants/status';


import { toEditorReservation } from '../lib/editorDataTransform';
import { reservationSyncService } from '../services/reservationSyncService';
import { PICKUP_SUB_ZONES } from './VehiclePositionMap';

// デフォルトの予約ステータス（propsが渡されない場合のフォールバック）
const DEFAULT_RESERVATION_STATUS_LIST = [];

const getColumns = (isMobile) => [
  { id: 'name', label: '名前', sticky: true, left: 0, minWidth: isMobile ? 120 : 150 },
  { id: 'vehicle', label: isMobile ? '予約一覧' : '車両', sticky: true, left: isMobile ? 120 : 150, minWidth: 100 },
  { id: 'time', label: '時間', sticky: !isMobile, left: 250, minWidth: 60 },
  { id: 'status', label: 'ステータス' },
  { id: 'pax', label: '人数' },
  { id: 'flight', label: 'ARR/DEP' },
  { id: 'location', label: '場所' },
  { id: 'license', label: '免許' },
  { id: 'payment', label: '支払' },
  { id: 'contact', label: '連絡' },
];

// 連絡手段マスタ
const COMMUNICATION_TOOLS = [
  { id: 1, label: 'TEL' },
  { id: 2, label: 'LINE' },
  { id: 3, label: 'SMS' },
  { id: 4, label: 'WhatsApp' },
  { id: 5, label: 'Instagram' },
  { id: 6, label: 'その他' },
];



export default function ReservationTable({
  title,
  data,
  type,
  vehicles,
  formatTime,
  onVehicleClick,
  onStatusClick,
  onLocationStatusClick,
  onReservationSave,
  onQuickUpdate,
  onStaffClick,
  onLicenseToggle,
  onCommunicationToolsUpdate,
  squareDataCache = {},
  squareDataLoading = false,
  onPaymentCheck,
  onBeforeOpenReservation, // 予約パネルを開く前に呼ばれるコールバック（trueを返すと開く、falseで中止）
  forceCloseReservationPanel, // 外部から予約パネルを閉じるためのフラグ
  reservationStatusList = [], // 動的に取得した予約ステータスリスト
  vehicleStatusList = [], // 動的に取得した車両ステータスリスト
  locationStatusList = [], // 動的に取得した車両移動ステータスリスト
  staffList = [], // スタッフリスト
  onCheckUpdate, // 車両チェック項目更新コールバック
  upcomingStatuses = {}, // 未来の予約状況
  allowCopy = true, // コピー機能を許可するかどうか
  onAssignClick, // 車両アサインクリックハンドラ
  carTypesList = [], // 車種リスト
  isSimpleView = false, // シンプル表示モード
  isToday = false, // 本日の日付を表示しているか
  onHoverVehicle, // ホバー時のコールバック
  onLeaveVehicle, // ホバー解除時のコールバック
  lineUsers = [], // LINEユーザー情報
  onOpenLineChat, // LINEチャットモーダルを開くコールバック
  hideSearch = false, // 検索フィールドを非表示にする
  onContactClick, // 「連絡」ボタンクリック
  hideToolbar = false, // ツールバー（タイトル+検索+設定）を非表示にする
  compactMode = false, // コンパクト表示（15件でスクロール）
  externalSearchTerm, // 外部から検索語を受け取る（hideToolbar 時）
  onSettingsOpen, // 外部から設定ボタンを制御
  onVehicleSwap = null, // 車両DnDスワップ: (sourceRow, targetRow) => void
  vehicleDragSource = null, // 外部管理のドラッグソース予約
  onVehicleDragSourceChange = null, // ドラッグソース変更コールバック: (row | null) => void
}) {
  const theme = useTheme();

  // Create lookup map for LINE linked reservations (reservation_id → LINE user)
  const lineUserMap = useMemo(() => {
    const map = {};
    if (Array.isArray(lineUsers)) {
      lineUsers.forEach(u => {
        // Map each linked reservation_id to this LINE user
        const rids = u.reservation_ids || [];
        rids.forEach(rid => {
          if (!String(rid).startsWith('STAFF:')) {
            map[String(rid)] = u;
          }
        });
        // Also support legacy system_user_id mapping
        if (u.system_user_id) {
          map[String(u.system_user_id)] = u;
        }
      });
    }
    return map;
  }, [lineUsers]);
  // Ensure same breakpoint as DayListReservations
  const isMobile = useMediaQuery('(max-width:800px)');
  const ALL_COLUMNS = React.useMemo(() => getColumns(isMobile), [isMobile]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [resStatusModal, setResStatusModal] = useState({ open: false, row: null });

  // propsが渡されない場合はデフォルト値を使用
  const statusOptions = useMemo(() => {
    return reservationStatusList?.length > 0 ? reservationStatusList : DEFAULT_RESERVATION_STATUS_LIST;
  }, [reservationStatusList]);

  // ステータスIDからコードへのマッピング（動的に生成）
  const statusIdToCode = useMemo(() => {
    const map = {};
    statusOptions.forEach(s => {
      if (s.id !== undefined && s.code) map[s.id] = s.code;
    });
    return map;
  }, [statusOptions]);

  // ステータスコードからIDへのマッピング（動的に生成）
  const statusCodeToId = useMemo(() => {
    const map = {};
    statusOptions.forEach(s => {
      if (s.id !== undefined && s.code) map[s.code] = s.id;
    });
    return map;
  }, [statusOptions]);

  // 車両ステータスコードからIDへのマッピング
  const vehicleStatusCodeToId = useMemo(() => {
    const map = {};
    vehicleStatusList.forEach(s => {
      if (s.id !== undefined && s.code) map[s.code] = s.id;
    });
    return map;
  }, [vehicleStatusList]);

  // Extract vehicleTypes from vehicles list for Editor
  const vehicleTypes = useMemo(() => {
    if (!vehicles || typeof vehicles !== 'object') return [];
    const vehicleList = Object.values(vehicles);
    const types = [];
    const seen = new Set();
    vehicleList.forEach((v: any) => {
      if (v.vehicle_type) {
        // Use ID or Code as unique key
        const key = v.vehicle_type.id;
        if (!seen.has(key)) {
          seen.add(key);
          types.push(v.vehicle_type);
        }
      }
    });
    return types;
  }, [vehicles]);

  // 外部からパネルを閉じる
  useEffect(() => {
    if (forceCloseReservationPanel) {
      setSelectedReservation(null);
    }
  }, [forceCloseReservationPanel]);

  // 予約パネルを開く処理
  const handleOpenReservation = (row) => {
    if (onBeforeOpenReservation) {
      const canOpen = onBeforeOpenReservation(row);
      if (!canOpen) return;
    }
    setSelectedReservation(row);
  };

  // 予約ステータス変更ハンドラ
  const handleResStatusUpdate = (statusId) => {
    if (resStatusModal.row) {
      // パネルを開く。ステータスを変更した状態で。
      const updated = { ...resStatusModal.row, status: statusId };
      setSelectedReservation(updated);
      setResStatusModal({ open: false, row: null });
    }
  };

  // Load initial state from localStorage
  const getInitialVisibleColumns = () => {
    // デフォルトは全列表示
    const defaultColumns = ALL_COLUMNS.reduce((acc, col) => ({ ...acc, [col.id]: true }), {});
    try {
      const saved = localStorage.getItem(`reservation_table_visible_${type}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        // 保存された設定と新規追加された列をマージ（新規列はデフォルトで表示）
        return { ...defaultColumns, ...parsed };
      }
    } catch (e) {
      console.error('Failed to load settings', e);
    }
    return defaultColumns;
  };

  const getInitialSelectedStatuses = () => {
    try {
      const saved = localStorage.getItem(`reservation_table_status_${type}`);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load settings', e);
    }
    return DEFAULT_RESERVATION_STATUS_LIST.reduce((acc, status) => ({ ...acc, [status.id]: true }), {});
  };

  const getInitialSeatVisibility = () => {
    const defaultVisibility = { CS: true, BS: true, JS: true };
    try {
      const saved = localStorage.getItem(`reservation_table_seat_visibility_${type}`);
      if (saved) {
        return { ...defaultVisibility, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.error('Failed to load seat visibility settings', e);
    }
    return defaultVisibility;
  };

  const [expandedRows, setExpandedRows] = useState({});

  const toggleRowExpansion = (rowId) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  const [visibleColumns, setVisibleColumns] = useState(getInitialVisibleColumns);
  const [selectedStatuses, setSelectedStatuses] = useState(getInitialSelectedStatuses);
  const [seatVisibility, setSeatVisibility] = useState(getInitialSeatVisibility);
  const [editingReservation, setEditingReservation] = useState(null);

  const [isSimpleViewInternal, setIsSimpleViewInternal] = useState(false); // Removed local state if prop is used, but keeping it for now if needed. 
  // Wait, let's use the prop directly.
  const actualVisibleColumns = isSimpleView
    ? { vehicle: true }
    : visibleColumns;

  // 連絡手段モーダル用state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalReservation, setContactModalReservation] = useState(null);
  const [selectedTools, setSelectedTools] = useState([]);

  // 車両チェックモーダル用state
  const [checkModalOpen, setCheckModalOpen] = useState(false);
  const [checkModalVehicle, setCheckModalVehicle] = useState(null);
  const [checkModalItems, setCheckModalItems] = useState([]);
  const [searchTermInternal, setSearchTermInternal] = useState('');
  const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : searchTermInternal;
  const setSearchTerm = externalSearchTerm !== undefined ? (() => {}) : setSearchTermInternal;

  // ショートメモ・時間更新モーダル用
  const [shortMemoModal, setShortMemoModal] = useState({ open: false, row: null, value: '' });
  const [requestTimeModal, setRequestTimeModal] = useState({ open: false, row: null, value: '', field: '' });

  useEffect(() => {
    if (selectedReservation) {
      // 共通ユーティリティでエディタ用データに変換
      setEditingReservation(toEditorReservation(selectedReservation));
    } else {
      setEditingReservation(null);
    }
  }, [selectedReservation]);

  const handleCopyReservation = (row) => {
    // 既存の予約をベースに新規予約オブジェクトを作成
    const currentCode = row.contact_code || '';
    const baseCode = currentCode.replace(/-(\d+)$/, '');

    // 既存の取得可能な全データから最大のスフィックスを計算
    const cachedData = reservationSyncService.getReservations();
    const allKnownReservations = [...(Array.isArray(data) ? data : []), ...cachedData];

    let maxSuffix = 1;
    if (baseCode) {
      const pattern = new RegExp(`^${baseCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:-(\\d+))?$`);
      allKnownReservations.forEach(r => {
        const code = r.contact_code;
        if (code) {
          const match = code.match(pattern);
          if (match) {
            const suffix = match[1] ? parseInt(match[1], 10) : 1;
            if (suffix > maxSuffix) maxSuffix = suffix;
          }
        }
      });
    }

    const nextCode = baseCode ? `${baseCode}-${maxSuffix + 1}` : '';

    const copied = {
      ...row,
      id: null, // IDをnullにすることで新規扱いにする
      contact_code: nextCode,
      __isNew: true, // 新規予約フラグ
      __isCopy: true // コピーフラグ
    };
    // パネルを開く
    setSelectedReservation(copied);
  };

  // Save to localStorage on change
  useEffect(() => {
    localStorage.setItem(`reservation_table_visible_${type}`, JSON.stringify(visibleColumns));
  }, [visibleColumns, type]);

  useEffect(() => {
    localStorage.setItem(`reservation_table_status_${type}`, JSON.stringify(selectedStatuses));
  }, [selectedStatuses, type]);

  useEffect(() => {
    localStorage.setItem(`reservation_table_seat_visibility_${type}`, JSON.stringify(seatVisibility));
  }, [seatVisibility, type]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleColumnToggle = (columnId) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  const handleStatusToggle = (statusId) => {
    setSelectedStatuses((prev) => {
      // 現在の状態を取得（undefinedの場合はデフォルトでtrueとする）
      const currentState = prev[statusId] !== false;
      return {
        ...prev,
        [statusId]: !currentState,
      };
    });
  };

  const handleSeatVisibilityToggle = (seatType) => {
    setSeatVisibility((prev) => ({
      ...prev,
      [seatType]: !prev[seatType],
    }));
  };

  const getColumnLabel = (col) => {
    if (col.id === 'flight') {
      return type === 'departure' ? 'ARR' : 'DEP';
    }
    if (col.id === 'pax') {
      const seatLabels = [];
      if (seatVisibility.CS) seatLabels.push('C');
      if (seatVisibility.BS) seatLabels.push('B');
      if (seatVisibility.JS) seatLabels.push('J');
      return <div><div>人数</div>{seatLabels.length > 0 && <div style={{ fontSize: '0.75rem' }}>{seatLabels.join('/')}</div>}</div>;
    }
    return col.label;
  };

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      // 1. Status Filter
      // ステータスIDまたはコードをIDに正規化
      let sId = row.status;
      if (typeof sId === 'string' && isNaN(Number(sId))) {
        sId = statusCodeToId[sId] || sId;
      }

      if (selectedStatuses[sId] === false) {
        return false;
      }

      // 2. Search Filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const vehicle = vehicles[row.vehicle_id] ?? {};

        // Search Targets
        const searchTargets = [
          row.customer_name,
          row.last_name,
          row.first_name,
          row.last_name_kana,
          row.first_name_kana,
          vehicle.name,
          vehicle.vehicle_code,
          vehicle.car_type,
          row.reservation_number, // data dependant
          String(row.id)
        ];

        const match = searchTargets.some(val =>
          val && String(val).toLowerCase().includes(term)
        );
        if (!match) return false;
      }

      return true;
    });
  }, [data, selectedStatuses, searchTerm, vehicles, reservationStatusList, statusCodeToId]);

  return (
    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {!hideToolbar && (<><Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          {title} ({filteredData.length})
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {!hideSearch && (
            <TextField
              size="small"
              placeholder="検索"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mr: 1, minWidth: 150 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          )}
          <IconButton onClick={handleMenuOpen} size="small">
            <SettingsIcon />
          </IconButton>
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            style: {
              maxHeight: 400,
              width: '25ch',
            },
          }}
        >
          <ListSubheader>表示項目</ListSubheader>
          {ALL_COLUMNS.map((col) => (
            <MenuItem key={col.id} dense>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={visibleColumns[col.id]}
                    onChange={() => handleColumnToggle(col.id)}
                    size="small"
                  />
                }
                label={getColumnLabel(col)}
              />
            </MenuItem>
          ))}
          <Divider />
          <ListSubheader>シート表示 (C/B/J)</ListSubheader>
          {['CS', 'BS', 'JS'].map((seatType) => (
            <MenuItem key={seatType} dense>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={seatVisibility[seatType]}
                    onChange={() => handleSeatVisibilityToggle(seatType)}
                    size="small"
                  />
                }
                label={seatType === 'CS' ? 'チャイルドシート (C)' : seatType === 'BS' ? 'ブースターシート (B)' : 'ジュニアシート (J)'}
              />
            </MenuItem>
          ))}
          <Divider />
          <ListSubheader>ステータスを選択</ListSubheader>
          {statusOptions.map((status) => (
            <MenuItem key={status.id} dense>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedStatuses[status.id] !== false}
                    onChange={() => handleStatusToggle(status.id)}
                    size="small"
                  />
                }
                label={status.label}
              />
            </MenuItem>
          ))}
        </Menu>
      </Box></>)}

      <TableContainer sx={{
        maxHeight: compactMode ? 540 : 'none',
        overflowY: compactMode ? 'auto' : 'visible',
        overflowX: isSimpleView ? 'hidden' : 'auto',
        border: '1px solid #ddd',
        borderRadius: 1
      }}>
        <Table stickyHeader size="small" sx={{ minWidth: isSimpleView ? 'auto' : { xs: 500, md: 650 }, borderCollapse: 'separate', borderSpacing: 0 }}>
          <TableHead>
            <TableRow>
              {ALL_COLUMNS.map((col) => (
                actualVisibleColumns[col.id] && (
                  <TableCell
                    key={col.id}
                    sx={{
                      whiteSpace: col.id === 'time' ? 'normal' : 'nowrap',
                      fontWeight: 'bold',
                      backgroundColor: type === 'return' ? '#424242' : '#f5f5f5',
                      color: type === 'return' ? '#ffffff' : 'inherit',
                      borderRight: 'none',
                      borderLeft: 'none',
                      position: 'sticky',
                      ...(col.sticky && {
                        left: col.left,
                        zIndex: 3,
                        minWidth: col.minWidth,
                        boxShadow: col.id === 'time' ? '2px 0 4px rgba(0,0,0,0.1)' : 'none',
                      })
                    }}
                  >
                    {getColumnLabel(col)}
                  </TableCell>
                )
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((row) => {
              const vehicle = vehicles[row.vehicle_id];
              // 車両ステータス情報の取得（IDまたはコードで検索）
              const vStatus = vehicle?.status;
              const statusInfo = vehicleStatusList?.find(s =>
                s.id === Number(vStatus) || (typeof vStatus === 'string' && s.code === vStatus)
              );
              // ステータスの色を優先する（車両自体の色はタグの方で使用）
              const vBgColor = statusInfo?.bg_color || vehicle?.bg_color || vehicle?.vehicle_type?.bg_color || '#e0e0e0';
              const vTextColor = statusInfo?.text_color || vehicle?.text_color || vehicle?.vehicle_type?.text_color || '#000';
              const time = type === 'departure' ? row.start_date : row.end_date;
              // NOTE: Kept for safety even though data is validated
              const detail = row.detail || {};
              const flight = type === 'departure' ? (row.arrival_flight || detail.arrival_flight) : (row.departure_flight || detail.departure_flight);
              const pax = row.pax || detail.pax;
              const name = row.customer_name || `${row.last_name} ${row.first_name}`;
              const displayName = name.length > 10 ? name.substring(0, 10) + '...' : name;
              const effectiveCarType = row.car_type || (vehicle?.vehicle_type?.vehicle_type_code);
              const matchedType = carTypesList.find(ct => ct.code === effectiveCarType);

              // 予約ステータス情報の取得
              const rStatus = row.status;
              const statusOpt = statusOptions.find(o =>
                o.id === Number(rStatus) || (typeof rStatus === 'string' && o.code === rStatus)
              );
              const statusName = row.status_name || (statusOpt ? statusOpt.label : '');

              // 予約が「処理済み（対応不要）」かどうか判定
              // 貸出リスト：利用中（出発済み）
              // 返却リスト：返却済み
              const isProcessedRes = (
                (type === 'departure' && (
                  rStatus === 3 ||
                  rStatus === '3' ||
                  rStatus === 4 ||
                  rStatus === '4' ||
                  statusName.includes('利用中') ||
                  statusName.includes('出発中') ||
                  statusName.includes('貸出済み') ||
                  statusName.includes('貸出済') ||
                  (statusOpt && (
                    statusOpt.label?.includes('利用中') ||
                    statusOpt.label?.includes('出発中') ||
                    statusOpt.label?.includes('貸出済み') ||
                    statusOpt.label?.includes('貸出済') ||
                    statusOpt.code === 'in_use' ||
                    statusOpt.code === 'done'
                  ))
                )) ||
                (type === 'return' && (
                  rStatus === 4 ||
                  rStatus === '4' ||
                  statusName.includes('貸出済み') ||
                  statusName.includes('貸出済') ||
                  (statusOpt && (
                    statusOpt.label?.includes('貸出済み') ||
                    statusOpt.label?.includes('貸出済') ||
                    statusOpt.code === 'done'
                  ))
                ))
              );

              // 予約データに個別の背景色設定がある場合はそれを最優先
              const hasResColor = row.bg_color && typeof row.bg_color === 'string' && row.bg_color.trim();

              const tagStyle = hasResColor ? {
                backgroundColor: row.bg_color.trim(),
                color: (row.text_color && typeof row.text_color === 'string' && row.text_color.trim()) ? row.text_color.trim() : '#000',
                display: 'inline-block', padding: '0.1em 0.3em', borderRadius: '3px', lineHeight: '1.2'
              } : (vehicle ? {
                backgroundColor: matchedType?.bg_color || vehicle.bg_color || vehicle.vehicle_type?.bg_color || '#eee',
                color: matchedType?.text_color || vehicle.text_color || vehicle.vehicle_type?.text_color || '#000',
                display: 'inline-block', padding: '0.1em 0.3em', borderRadius: '3px', lineHeight: '1.2'
              } : {
                backgroundColor: matchedType?.bg_color || '#eee',
                color: matchedType?.text_color || '#000',
                display: 'inline-block', padding: '0.1em 0.3em', borderRadius: '3px', lineHeight: '1.2'
              });

              // 車両自体の本来の色（予約の個別色は無視する）
              const vehicleOriginalStyle = vehicle ? {
                backgroundColor: matchedType?.bg_color || vehicle.bg_color || vehicle.vehicle_type?.bg_color || '#eee',
                color: matchedType?.text_color || vehicle.text_color || vehicle.vehicle_type?.text_color || '#000',
                display: 'inline-block', padding: '0.1em 0.3em', borderRadius: '3px', lineHeight: '1.2'
              } : {
                backgroundColor: matchedType?.bg_color || '#eee',
                color: matchedType?.text_color || '#000',
                display: 'inline-block', padding: '0.1em 0.3em', borderRadius: '3px', lineHeight: '1.2'
              };

              // スタッフ情報を取得
              // 貸出: pu_staff（運転者）, rent_staff（対応者）
              // 返却: do_staff（運転者）, return_staff（対応者）
              const driverStaffRaw = type === 'departure' ? row.pu_staff : row.do_staff;
              const handlerStaffRaw = type === 'departure' ? row.rent_staff : row.return_staff;

              let driverStaff = (driverStaffRaw && Array.isArray(staffList)) ? (staffList.find(s => s.id === driverStaffRaw.id) || driverStaffRaw) : driverStaffRaw;
              const handlerStaff = (handlerStaffRaw && Array.isArray(staffList)) ? (staffList.find(s => s.id === handlerStaffRaw.id) || handlerStaffRaw) : handlerStaffRaw;

              // ID:14（スポットスタッフ）の場合: メモからアサイン名を取得（見つかった場合のみ上書き）
              if (driverStaff && driverStaff.id === 14) {
                const memo = row.detail?.memo || row.memo || '';
                const assignLabel = type === 'departure' ? '貸出運転者アサイン' : '返却運転者アサイン';
                const memoRegex = new RegExp(`---${assignLabel} [\\d-]+ [\\d:]+---\\n(.+?)\\n---`);
                const match = memo.match(memoRegex);
                if (match) {
                  driverStaff = { ...driverStaff, staff_name: match[1], name: match[1] };
                }
              }

              const contactPlan = row.contact_plan || detail.contact_plan || '';
              let planPrefix = '';
              if (contactPlan.includes('セルフ')) {
                planPrefix = '🚶‍♀️';
              } else if (contactPlan.includes('ステーション')) {
                planPrefix = '🚃';
              }

              const isAgreement = Number(detail.agreement) === 1 || detail.agreement === true;
              const isLicense = Number(detail.license) === 1 || detail.license === true;
              const isPayment = Number(detail.payment) === 1 || detail.payment === true;

              const checkCount = (isAgreement ? 1 : 0) + (isLicense ? 1 : 0) + (isPayment ? 1 : 0);
              let borderLeftColor = '#7e7e7e';
              if (checkCount === 3) {
                borderLeftColor = '#00ac00';
              } else if (checkCount > 0) {
                borderLeftColor = '#ffe747';
              }

              const isExpanded = expandedRows[row.id];


              // --- 共通コンポーネントの定義 ---
              const agreementIcon = (
                <Tooltip title={`事前受付: ${isAgreement ? '完了' : '未完了'}`} arrow>
                  <span style={{ fontSize: '0.85rem', lineHeight: 1, opacity: isAgreement ? 1 : 0.15, paddingBottom: '1px', filter: isAgreement ? 'none' : 'grayscale(1)' }}>📄</span>
                </Tooltip>
              );
              const licenseIcon = (
                <Tooltip title={`免許証: ${isLicense ? '完了' : '未完了'}`} arrow>
                  <span style={{ fontSize: '0.85rem', lineHeight: 1, opacity: isLicense ? 1 : 0.15, paddingBottom: '1px', filter: isLicense ? 'none' : 'grayscale(1)' }}>🪪</span>
                </Tooltip>
              );
              const isPkg = (
                (detail.contact_media && String(detail.contact_media).toUpperCase().includes('PKG')) ||
                (detail.plan_name && String(detail.plan_name).toUpperCase().includes('PKG')) ||
                (row.plan_name && String(row.plan_name).toUpperCase().includes('PKG'))
              );

              const paymentIcon = (
                <Tooltip title={(!isPayment && isPkg) ? "パッケージ" : `支払い: ${isPayment ? '完了' : '未完了'}`} arrow>
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onPaymentCheck) onPaymentCheck(row);
                    }}
                    style={{
                      fontSize: '0.85rem',
                      lineHeight: 1,
                      opacity: (isPayment || isPkg) ? 1 : 0.15,
                      filter: (isPayment || isPkg) ? 'none' : 'grayscale(1)',
                      display: 'inline-block',
                      width: '1em',
                      textAlign: 'center',
                      fontWeight: 'bold',
                      color: (!isPayment && isPkg) ? '#1976d2' : 'inherit',
                      cursor: onPaymentCheck ? 'pointer' : 'default'
                    }}
                  >
                    {(!isPayment && isPkg) ? 'P' : '💰'}
                  </span>
                </Tooltip>
              );

              const lineUserData = lineUserMap[String(row.id)];

              const nameSection = (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => { e.stopPropagation(); handleOpenReservation(row); }}>
                  <Tooltip title={lineUserData ? (lineUserData.unread_count > 0 ? `LINE (未読 ${lineUserData.unread_count}件)` : 'LINEチャットを開く') : 'LINE未連携'} arrow>
                    <Box
                      onClick={(e) => { e.stopPropagation(); if (lineUserData && onOpenLineChat) onOpenLineChat(lineUserData, row.id); }}
                      sx={{
                        position: 'relative', display: 'flex', alignItems: 'center',
                        cursor: lineUserData ? 'pointer' : 'default',
                        '&:hover': lineUserData ? { transform: 'scale(1.15)' } : {},
                        transition: 'transform 0.1s',
                        opacity: lineUserData ? 1 : 0.25,
                      }}
                    >
                      <span style={{
                        fontSize: '1.2rem',
                        lineHeight: 1,
                        color: lineUserData ? '#06C755' : '#9e9e9e',
                        filter: lineUserData?.unread_count > 0 ? 'drop-shadow(0 0 2px #06C755)' : 'none'
                      }}>💬</span>
                      {lineUserData?.unread_count > 0 && (
                        <Box sx={{
                          position: 'absolute',
                          top: -2,
                          right: -4,
                          width: 8,
                          height: 8,
                          bgcolor: '#ff4d4d',
                          borderRadius: '50%',
                          border: '1px solid #fff'
                        }} />
                      )}
                    </Box>
                  </Tooltip>
                  <Tooltip title={name} arrow><span style={{ ...tagStyle, fontWeight: 'bold' }}>{displayName}</span></Tooltip>
                  {planPrefix && <span>{planPrefix}</span>}
                  {row.short_memo && (
                    <Tooltip title={row.short_memo} arrow>
                      <span
                        onClick={(e) => { e.stopPropagation(); setShortMemoModal({ open: true, row: row, value: row.short_memo || '' }); }}
                        style={{ cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1 }}
                      >⚠️</span>
                    </Tooltip>
                  )}
                  {allowCopy && (
                    <Tooltip title="コピー" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleCopyReservation(row); }}
                        sx={{ ml: 0.5, p: 0, color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                      >
                        <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );

              // 連絡ボタンのラベルと色（空港サブゾーン or 連絡）
              const contactJsonData = row.json_data || row.detail?.json_data;
              const hasContactArrival = contactJsonData?.data?.['到着連絡'] === '有り';
              const contactAirportValue = contactJsonData?.data?.['空港'];
              const matchedSubZone = PICKUP_SUB_ZONES.find(sz => sz.airportValue === contactAirportValue);
              const contactLabel = hasContactArrival
                ? (contactAirportValue && contactAirportValue !== '未'
                  ? (contactAirportValue === '直接来店' ? '直来' : contactAirportValue)
                  : '連絡')
                : '';
              const contactBgColor = (hasContactArrival && matchedSubZone && contactAirportValue !== '未')
                ? matchedSubZone.borderColor : (hasContactArrival ? '#00695c' : undefined);
              const contactTextColor = (hasContactArrival && matchedSubZone && (matchedSubZone as any).textColor)
                ? (matchedSubZone as any).textColor : '#fff';

              const staffButtons = (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                  {onContactClick && (
                    <Button
                      variant={hasContactArrival ? "contained" : "outlined"}
                      size="small"
                      onClick={(e) => { e.stopPropagation(); onContactClick(row); }}
                      sx={{
                        fontSize: '0.6rem', padding: '2px 6px', minWidth: 'auto', boxShadow: 'none',
                        ...(hasContactArrival
                          ? { backgroundColor: contactBgColor, color: contactTextColor, '&:hover': { backgroundColor: contactBgColor, filter: 'brightness(0.9)' } }
                          : { borderColor: '#bdbdbd', color: '#757575', '&:hover': { borderColor: '#9e9e9e' } }
                        ),
                      }}
                    >
                      {contactLabel || '連絡'}
                    </Button>
                  )}
                  <Button
                    variant={driverStaff?.bg_color ? "contained" : "outlined"}
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onStaffClick && onStaffClick(row, 'driver', type); }}
                    sx={{
                      fontSize: '0.65rem', padding: '2px 8px', minWidth: 'auto', boxShadow: 'none',
                      backgroundColor: driverStaff?.bg_color || (driverStaff ? '#e8f5e9' : '#fff'),
                      borderColor: driverStaff?.text_color || (driverStaff ? '#4caf50' : '#bdbdbd'),
                      color: driverStaff?.text_color || (driverStaff ? '#2e7d32' : '#757575'),
                      '&:hover': { backgroundColor: driverStaff?.bg_color ? `${driverStaff.bg_color}dd` : (driverStaff ? '#c8e6c9' : '#f5f5f5'), borderColor: driverStaff?.text_color || (driverStaff ? '#4caf50' : '#9e9e9e'), filter: driverStaff?.bg_color ? 'brightness(0.95)' : 'none' }
                    }}
                  >
                    {driverStaff ? (() => {
                      const name = driverStaff.staff_name || driverStaff.name || '';
                      return name.length > 4 ? name.slice(0, 4) + '...' : name;
                    })() : '運転者'}
                  </Button>
                  <Button
                    variant={handlerStaff?.bg_color ? "contained" : "outlined"}
                    size="small"
                    onClick={(e) => { e.stopPropagation(); onStaffClick && onStaffClick(row, 'handler', type); }}
                    sx={{
                      fontSize: '0.65rem', padding: '2px 8px', minWidth: 'auto', boxShadow: 'none',
                      backgroundColor: handlerStaff?.bg_color || (handlerStaff ? '#fff3e0' : '#fff'),
                      borderColor: handlerStaff?.text_color || (handlerStaff ? '#ff9800' : '#bdbdbd'),
                      color: handlerStaff?.text_color || (handlerStaff ? '#e65100' : '#757575'),
                      '&:hover': { backgroundColor: handlerStaff?.bg_color ? `${handlerStaff.bg_color}dd` : (handlerStaff ? '#ffe0b2' : '#f5f5f5'), borderColor: handlerStaff?.text_color || (handlerStaff ? '#ff9800' : '#9e9e9e'), filter: handlerStaff?.bg_color ? 'brightness(0.95)' : 'none' }
                    }}
                  >
                    {handlerStaff ? (() => {
                      const name = handlerStaff.staff_name || handlerStaff.name || '';
                      return name.length > 4 ? name.slice(0, 4) + '...' : name;
                    })() : '対応者'}
                  </Button>
                </Box>
              );


              const isDragSource = vehicleDragSource && String(row.id) === String(vehicleDragSource.id);
              const isValidDropTarget = vehicleDragSource && String(row.id) !== String(vehicleDragSource.id) && vehicle;
              const vehicleTag = (
                <span
                  draggable={!!vehicle && !!onVehicleSwap}
                  onDragStart={(e) => {
                    if (!vehicle || !onVehicleSwap) return;
                    e.stopPropagation();
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', vehicle.vehicle_code);
                    onVehicleDragSourceChange?.(row);
                  }}
                  onDragOver={(e) => {
                    if (isValidDropTarget) e.preventDefault();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (vehicleDragSource && vehicle && String(row.id) !== String(vehicleDragSource.id)) {
                      onVehicleSwap?.(vehicleDragSource, row);
                    }
                    onVehicleDragSourceChange?.(null);
                  }}
                  onDragEnd={() => onVehicleDragSourceChange?.(null)}
                  onClick={(e) => { e.stopPropagation(); vehicle && onVehicleClick && onVehicleClick(vehicle, row); }}
                  style={{
                    ...vehicleOriginalStyle,
                    cursor: vehicle && onVehicleSwap ? 'grab' : vehicle ? 'pointer' : 'default',
                    fontWeight: vehicle ? 'bold' : 'normal',
                    opacity: isDragSource ? 0.4 : 1,
                    outline: isValidDropTarget ? '2px dashed #3b82f6' : 'none',
                    outlineOffset: '2px',
                    borderRadius: isValidDropTarget ? '4px' : undefined,
                    transition: 'outline 0.15s, opacity 0.15s',
                  }}
                >
                  {vehicle ? vehicle.vehicle_code : row.vehicle_id}
                </span>
              );

              const checkButton = vehicle && (Array.isArray(vehicle.check_situations) && (() => {
                const requiredItems = vehicle.check_situations.filter(item => item.require_flg === true);
                if (requiredItems.length === 0) return null;
                const allComplete = requiredItems.every(item => item.value === true);
                const nonVcRequiredItems = requiredItems.filter(item => !item.name?.toUpperCase()?.includes('VC'));
                const isVcMode = !allComplete && nonVcRequiredItems.length > 0 && nonVcRequiredItems.every(item => item.value === true);
                const incompleteItems = requiredItems.filter(item => item.value !== true);
                const tooltipContent = incompleteItems.length > 0 ? incompleteItems.map(item => `${item.name}: 未`).join('\n') : '全て完了';

                // VC以外の未完了項目を判定（清掃・洗車の単独残り表示）
                const nonVcIncomplete = nonVcRequiredItems.filter(item => item.value !== true);
                const isCleaningOnly = !allComplete && !isVcMode && nonVcIncomplete.length === 1 && (nonVcIncomplete[0].name?.includes('清掃') || nonVcIncomplete[0].name?.toLowerCase()?.includes('cleaning'));
                const isCarWashOnly = !allComplete && !isVcMode && nonVcIncomplete.length === 1 && (nonVcIncomplete[0].name?.includes('洗車') || nonVcIncomplete[0].name?.toLowerCase()?.includes('car_wash') || nonVcIncomplete[0].name?.toLowerCase()?.includes('wash'));

                // ラベル・色の決定
                let checkLabel = '未';
                let checkBg = '#fdecea';
                let checkBorder = '#d32f2f';
                let checkColor = '#d32f2f';
                let checkHoverBg = '#ffcdd2';
                if (allComplete) {
                  checkLabel = 'OK'; checkBg = '#e8f5e9'; checkBorder = '#2e7d32'; checkColor = '#2e7d32'; checkHoverBg = '#c8e6c9';
                } else if (isCleaningOnly) {
                  checkLabel = '清掃'; checkBg = '#fff3e0'; checkBorder = '#e65100'; checkColor = '#e65100'; checkHoverBg = '#ffe0b2';
                } else if (isCarWashOnly) {
                  checkLabel = '洗車'; checkBg = '#e0f7fa'; checkBorder = '#00838f'; checkColor = '#00838f'; checkHoverBg = '#b2ebf2';
                } else if (isVcMode) {
                  checkLabel = 'VC'; checkBg = '#e3f2fd'; checkBorder = '#1976d2'; checkColor = '#1976d2'; checkHoverBg = '#bbdefb';
                }

                return (
                  <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{tooltipContent}</Box>} arrow>
                    <Button
                      variant="outlined" size="small"
                      onClick={(e) => { e.stopPropagation(); setCheckModalVehicle(vehicle); setCheckModalItems(vehicle.check_situations.map(item => ({ ...item }))); setCheckModalOpen(true); }}
                      sx={{
                        minWidth: '28px', padding: '2px 6px', fontSize: '0.65rem', fontWeight: 'bold',
                        backgroundColor: checkBg,
                        borderColor: checkBorder,
                        color: checkColor,
                        '&:hover': { backgroundColor: checkHoverBg, borderColor: checkBorder }
                      }}
                    >
                      {checkLabel}
                    </Button>
                  </Tooltip>
                );
              })());

              const vehicleStatusButton = vehicle && (
                <Button
                  variant="contained" size="small"
                  onClick={(e) => { e.stopPropagation(); onStatusClick && onStatusClick(vehicle, row); }}
                  sx={{
                    fontSize: '0.65rem', padding: '2px 8px', minWidth: 'auto', boxShadow: 'none',
                    backgroundColor: vBgColor, color: vTextColor,
                    '&:hover': { boxShadow: '0 1px 2px rgba(0,0,0,0.2)', backgroundColor: vBgColor, filter: 'brightness(0.9)' }
                  }}
                >
                  {vehicle.status_name || '未設定'}
                </Button>
              );

              const locationStatusButton = vehicle && (
                <Button
                  variant={(() => {
                    const status = locationStatusList?.find(s => s.id === Number(vehicle.location_status));
                    return status?.bg_color ? "contained" : "outlined";
                  })()}
                  size="small"
                  onClick={(e) => { e.stopPropagation(); onLocationStatusClick && onLocationStatusClick(vehicle, type, row); }}
                  sx={{
                    fontSize: '0.65rem', padding: '2px 6px', minWidth: 'auto',
                    ...(() => {
                      const status = locationStatusList?.find(s => s.id === Number(vehicle.location_status));
                      if (status?.bg_color) {
                        return {
                          backgroundColor: status.bg_color, borderColor: status.bg_color, color: status.text_color || '#fff',
                          '&:hover': { backgroundColor: `${status.bg_color}dd`, borderColor: status.bg_color, filter: 'brightness(0.95)' }
                        };
                      }
                      return {
                        backgroundColor: vehicle.location_status ? '#e3f2fd' : '#f5f5f5',
                        borderColor: vehicle.location_status ? '#1565c0' : '#bdbdbd',
                        color: vehicle.location_status ? '#1565c0' : '#757575',
                        '&:hover': { backgroundColor: vehicle.location_status ? '#bbdefb' : '#eeeeee', borderColor: vehicle.location_status ? '#1565c0' : '#9e9e9e' }
                      };
                    })(),
                    fontWeight: 500,
                    ...(vehicle.location_status && vehicle.location_status_name && vehicle.location_status_name !== '-' && {
                      animation: `${keyframes`0%, 100% { opacity: 1; } 50% { opacity: 0.4; }`} 3s ease-in-out infinite`
                    }),
                  }}
                >
                  {vehicle.location_status_name || '-'}
                </Button>
              );

              // Square Payment Section
              const contactId = row.contact_code || detail.contact_id;
              const invoices = squareDataCache[contactId] ?? [];
              const sumPrice = row.total_amount || detail.sum_price;
              const paidInvoices = invoices.filter(inv => inv.invoice_status === 'PAID');
              const totalPaidAmount = paidInvoices.reduce((sum, inv) => {
                const amount = inv.total_money?.amount || 0;
                return sum + Math.round(amount / 100);
              }, 0);
              const hasPaid = invoices.some(inv => inv.invoice_status === 'PAID');
              const hasUnpaid = invoices.some(inv => inv.invoice_status === 'UNPAID' || inv.invoice_status === 'DRAFT');
              const hasPartialPaid = invoices.some(inv => inv.invoice_status === 'PARTIALLY_PAID');
              let paymentStatusText = '';
              let paymentStatusColor = '#757575';
              const isManualPaid = Number(detail.payment) === 1;
              if (squareDataLoading) {
                paymentStatusText = '...';
              } else if (hasPaid && !hasUnpaid && !hasPartialPaid) {
                paymentStatusText = 'SQ済'; paymentStatusColor = '#4caf50';
              } else if (isManualPaid) {
                paymentStatusText = '済'; paymentStatusColor = '#4caf50';
              } else if (hasPartialPaid) {
                paymentStatusText = '部分'; paymentStatusColor = '#2196f3';
              } else if (hasUnpaid) {
                paymentStatusText = '未払'; paymentStatusColor = '#ff9800';
              }

              const paymentStatusComp = (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    cursor: onPaymentCheck ? 'pointer' : 'default',
                    padding: '4px',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.04)'
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onPaymentCheck) onPaymentCheck(row);
                  }}
                >
                  {(invoices.length > 0 || paymentStatusText) ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{totalPaidAmount > 0 ? totalPaidAmount.toLocaleString() : '-'}</Typography>
                      {paymentStatusText && (
                        <Typography variant="caption" sx={{ color: paymentStatusColor, fontWeight: 'bold', fontSize: '0.65rem' }}>{paymentStatusText}</Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {squareDataLoading ? '...' : '-'}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>({sumPrice ? Number(sumPrice).toLocaleString() : '-'})</Typography>
                </Box>
              );

              if (isSimpleView) {
                return (
                  <React.Fragment key={row.id}>
                     <TableRow
                      hover
                      onClick={() => toggleRowExpansion(row.id)}
                      onMouseEnter={() => onHoverVehicle && onHoverVehicle(row.vehicle_id)}
                      onMouseLeave={() => onLeaveVehicle && onLeaveVehicle()}
                      sx={{
                        backgroundColor: isExpanded ? '#f0f7ff' : (type === 'return' ? '#f5f5f5' : 'inherit'),
                        cursor: 'pointer',
                        opacity: (isProcessedRes && isToday) ? 0.65 : 1,
                        '&:hover': {
                          opacity: 1
                        }
                      }}
                    >
                      <TableCell sx={{ borderLeft: `5px solid ${borderLeftColor}`, py: 1.5, px: '10px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, overflow: 'hidden' }}>
                            {!isExpanded && visibleColumns.time && (
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', minWidth: '45px', whiteSpace: 'nowrap' }}>
                                {formatTime(time)}
                              </Typography>
                            )}
                            {visibleColumns.vehicle && vehicleTag}
                            {/* Icons moved to edges or removed from here as per request */}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {!isExpanded && (
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                {visibleColumns.vehicle && (
                                  <>
                                    {checkButton}
                                    {vehicleStatusButton}
                                    {locationStatusButton}
                                  </>
                                )}
                              </Box>
                            )}
                            {isExpanded ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />}
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ p: 0, borderBottom: isExpanded ? 'none' : 'inherit' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderBottom: '1px solid #ddd' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {/* Layout similar to the image */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {(visibleColumns.name || visibleColumns.license || visibleColumns.payment) && (
                                    <>
                                      <Box>
                                        {visibleColumns.name && (
                                          <>
                                            {nameSection}
                                            <Box sx={{ mt: 1 }}>
                                              {staffButtons}
                                            </Box>
                                          </>
                                        )}
                                      </Box>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', mt: 0.4 }}>
                                        {agreementIcon}
                                        {visibleColumns.license && licenseIcon}
                                        {visibleColumns.payment && paymentIcon}
                                      </Box>
                                    </>
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                  {visibleColumns.vehicle && (
                                    <>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {vehicleTag}
                                      </Box>
                                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {checkButton}
                                        {vehicleStatusButton}
                                        {locationStatusButton}
                                      </Box>
                                    </>
                                  )}
                                </Box>
                              </Box>

                              {/* Details specific to full view */}
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1, pt: 1, borderTop: '1px dashed #ccc' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    {visibleColumns.time && (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">時間: </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{formatTime(time)}</Typography>
                                      </Box>
                                    )}
                                    {visibleColumns.pax && (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">人数: </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{pax}{detail.pax_children ? `(${detail.pax_children})` : ''}</Typography>
                                      </Box>
                                    )}
                                    {visibleColumns.flight && flight && (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">{type === 'departure' ? 'ARR' : 'DEP'}: </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{flight}</Typography>
                                      </Box>
                                    )}
                                    {visibleColumns.status && (
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">ステータス: </Typography>
                                        <Typography variant="body2">
                                          <span style={{ backgroundColor: statusOpt?.bg_color || '#eee', color: statusOpt?.text_color || '#000', padding: '1px 4px', borderRadius: '3px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                            {statusName}
                                          </span>
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                  {visibleColumns.payment && paymentStatusComp}
                                </Box>

                                {visibleColumns.contact && (
                                  <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" color="text.secondary">連絡手段: </Typography>
                                    {(() => {
                                      const tools = Array.isArray(detail.communication_tools) ? detail.communication_tools : [];
                                      const PRIORITY = ['LINE', 'WhatsApp', 'SMS', 'TEL', 'Instagram', 'その他'];
                                      const selected = tools
                                        .map(id => COMMUNICATION_TOOLS.find(t => t.id === id))
                                        .filter(Boolean)
                                        .sort((a, b) => PRIORITY.indexOf(a.label) - PRIORITY.indexOf(b.label));

                                      const topTool = selected[0];
                                      const colors = topTool ? (COMMUNICATION_MEDIA_COLORS[topTool.label] || COMMUNICATION_MEDIA_COLORS.default) : null;

                                      return (
                                        <Button
                                          variant={topTool ? "contained" : "outlined"}
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setContactModalReservation(row);
                                            setSelectedTools(tools);
                                            setContactModalOpen(true);
                                          }}
                                          sx={{
                                            fontSize: '0.65rem', padding: '2px 8px', minWidth: 'auto', boxShadow: 'none',
                                            ...(topTool ? { backgroundColor: colors.bg_color, color: colors.text_color, borderColor: colors.bg_color } : { borderColor: '#bdbdbd', color: '#424242' })
                                          }}
                                        >
                                          {topTool ? (selected.length > 1 ? `${topTool.label} +${selected.length - 1}` : topTool.label) : '未設定'}
                                        </Button>
                                      );
                                    })()}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              }

              return (
                <TableRow
                  key={row.id}
                  hover
                  onMouseEnter={() => onHoverVehicle && onHoverVehicle(row.vehicle_id)}
                  onMouseLeave={() => onLeaveVehicle && onLeaveVehicle()}
                  sx={{
                    backgroundColor: type === 'return' ? '#f5f5f5' : 'inherit',
                    opacity: (isProcessedRes && isToday) ? 0.65 : 1,
                    '&:hover': {
                      opacity: 1,
                      '& .MuiTableCell-root': {
                        opacity: 1
                      }
                    }
                  }}
                >
                  {actualVisibleColumns.name && (
                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        color: 'inherit',
                        position: 'sticky',
                        left: 0,
                        zIndex: 2, // 確実に上にくるように調整
                        backgroundColor: type === 'return' ? '#f5f5f5' : '#fff',
                        opacity: (isProcessedRes && isToday) ? 0.65 : 1, // stickyセルにも透過を適用
                        minWidth: 150,
                        borderLeft: `5px solid ${borderLeftColor}`,
                      }}
                    >
                      {/* ターンアラウンド用ドット (返却側: 左) */}
                      {type === 'return' && row.rowAttention?.color === '#d32f2f' && (
                        <Box
                          className="turnaround-dot"
                          data-vehicle-id={row.vehicle_id}
                          data-type="return"
                          sx={{
                            position: 'absolute',
                            left: '-14px', // 5pxボーダーの外端（中心を-5pxにする: -5 - 9）
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 1,
                            width: 18,
                            height: 18,
                            opacity: 0,
                            pointerEvents: 'none'
                          }}
                        />
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                          {nameSection}
                          {/* 運転者・対応者ボタン */}
                          <Box sx={{ mt: 0.5 }}>
                            {staffButtons}
                          </Box>
                        </Box>

                        {/* ステータスアイコンを縦に並べるボックス */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '2px',
                            borderRadius: '4px',
                            padding: '2px 0',
                            mt: 0.2
                          }}
                        >
                          {agreementIcon}
                          {licenseIcon}
                          {paymentIcon}
                        </Box>
                      </Box>
                    </TableCell>
                  )}
                  {actualVisibleColumns.vehicle && (
                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        position: 'sticky',
                        left: isMobile ? 120 : 150,
                        zIndex: 1,
                        backgroundColor: type === 'return' ? '#f5f5f5' : '#fff',
                        opacity: (isProcessedRes && isToday) ? 0.65 : 1,
                        minWidth: 100,
                        boxShadow: '2px 0 4px rgba(0,0,0,0.05)',
                      }}
                    >
                      <Box>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          {/* ステータスアイコンを左側に配置 */}
                          {(() => {
                            const att = type === 'return' ? (row.rowAttention || (vehicle && upcomingStatuses?.[vehicle.id])) : null;
                            const isTurnaround = type === 'departure' && row.isTurnaroundDeparture;

                            if (att && !(row.rowAttention === null && row.hasOwnProperty('rowAttention'))) {
                              return (
                                <Tooltip title={att.label} arrow>
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 16,
                                      height: 16,
                                      minWidth: 16,
                                      borderRadius: '50%',
                                      backgroundColor: att.color,
                                      color: '#fff',
                                      fontSize: '11px',
                                      fontWeight: 'bold',
                                      fontFamily: 'serif',
                                      cursor: 'help'
                                    }}
                                  >
                                    i
                                  </Box>
                                </Tooltip>
                              );
                            } else if (isTurnaround) {
                              return (
                                <Tooltip title="当日返却あり（ターンアラウンド）" arrow>
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 16,
                                      height: 16,
                                      minWidth: 16,
                                      borderRadius: '50%',
                                      backgroundColor: '#d32f2f',
                                      color: '#fff',
                                      fontSize: '12px',
                                      fontWeight: 'bold',
                                      cursor: 'help'
                                    }}
                                  >
                                    !
                                  </Box>
                                </Tooltip>
                              );
                            } else {
                              // アイコンを持たない車両用の薄いグレーのプレースホルダー
                              return (
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    width: 16,
                                    height: 16,
                                    minWidth: 16,
                                    borderRadius: '50%',
                                    backgroundColor: '#f5f5f5',
                                    color: '#f5f5f5', // ほとんど見えないがスペースは確保
                                    fontSize: '11px',
                                    visibility: 'visible'
                                  }}
                                >
                                  i
                                </Box>
                              );
                            }
                          })()}
                          {vehicleTag}
                        </div>
                        {vehicle && (
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'nowrap', alignItems: 'center' }}>
                            {checkButton}
                            {vehicleStatusButton}
                            {locationStatusButton}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {actualVisibleColumns.time && (
                    <TableCell
                      align="center"
                      sx={{
                        position: 'sticky',
                        left: 250,
                        zIndex: 1,
                        backgroundColor: type === 'return' ? '#f5f5f5' : '#fff',
                        opacity: (isProcessedRes && isToday) ? 0.65 : 1,
                        minWidth: 60,
                        boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
                        textAlign: 'center',
                        paddingLeft: 0,
                        paddingRight: 0,
                      }}
                    >
                      {formatTime(time)}
                      {/* Request Time Display */}
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          const fieldName = type === 'departure' ? 'request_coming_time' : 'request_returning_time';
                          const val = row[fieldName] || '';
                          setRequestTimeModal({ open: true, row: row, value: val, field: fieldName });
                        }}
                        style={{
                          fontSize: '0.75rem',
                          color: '#1b5e20', // Dark Green
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          marginTop: '2px',
                          textAlign: 'center',
                          border: '1px dashed #a5d6a7',
                          borderRadius: '4px',
                          padding: '0 2px',
                          width: 'fit-content',
                          margin: '2px auto 0'
                        }}
                      >
                        {(() => {
                          const t = type === 'departure' ? row.request_coming_time : row.request_returning_time;
                          return t ? t.slice(0, 5) : '--:--';
                        })()}
                      </div>
                    </TableCell>
                  )}
                  {actualVisibleColumns.status && (
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {(() => {
                        return (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              setResStatusModal({ open: true, row: row });
                            }}
                            style={{
                              backgroundColor: statusOpt?.bg_color || '#eee',
                              color: statusOpt?.text_color || '#000',
                              display: 'inline-block',
                              padding: '0.1em 0.3em',
                              borderRadius: '3px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              cursor: 'pointer'
                            }}
                          >
                            {row.status_name || (statusOpt ? statusOpt.label : row.status)}
                          </span>
                        );
                      })()}
                    </TableCell>
                  )}
                  {actualVisibleColumns.pax && (
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <div>{pax}{detail.pax_children ? `(${detail.pax_children})` : ''}</div>
                      <div style={{ fontSize: '0.75rem', color: '#666' }}>
                        {(() => {
                          const parts = [];
                          if (seatVisibility.CS) parts.push(<span key="cs">C<strong>{detail.child_seat || 0}</strong></span>);
                          if (seatVisibility.BS) parts.push(<span key="bs">B<strong>{detail.booster_seat || 0}</strong></span>);
                          if (seatVisibility.JS) parts.push(<span key="js">J<strong>{detail.junior_seat || 0}</strong></span>);
                          return parts.length > 0 ? parts.reduce((prev, curr, i) => i === 0 ? [curr] : [...prev, '/', curr], []) : null;
                        })()}
                      </div>
                    </TableCell>
                  )}
                  {actualVisibleColumns.flight && (
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {flight}
                    </TableCell>
                  )}
                  {actualVisibleColumns.location && (
                    <TableCell sx={{ whiteSpace: 'nowrap', position: 'relative' }}>
                      {/* ターンアラウンド用ドロップアンカー (貸出側: 右) */}
                      {type === 'departure' && row.isTurnaroundDeparture && (
                        <Box
                          className="turnaround-dot"
                          data-vehicle-id={row.vehicle_id}
                          data-type="departure"
                          sx={{
                            position: 'absolute',
                            right: '-9px', // セルの右端ぴったり（中心を0にする）
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 1,
                            width: 18,
                            height: 18,
                            opacity: 0, // 透明なアンカー
                            pointerEvents: 'none'
                          }}
                        />
                      )}
                      -
                    </TableCell>
                  )}
                  {actualVisibleColumns.license && (
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {(() => {
                        const isChecked = Number(detail.license) === 1;
                        return (
                          <Button
                            variant={isChecked ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => onLicenseToggle && onLicenseToggle(row, type)}
                            sx={{
                              fontSize: '0.65rem',
                              padding: '2px 8px',
                              minWidth: 'auto',
                              boxShadow: 'none',
                              backgroundColor: isChecked ? '#4caf50' : '#fff',
                              borderColor: isChecked ? '#4caf50' : '#bdbdbd',
                              color: isChecked ? '#fff' : '#757575',
                              '&:hover': {
                                backgroundColor: isChecked ? '#388e3c' : '#f5f5f5',
                                borderColor: isChecked ? '#388e3c' : '#9e9e9e'
                              }
                            }}
                          >
                            {isChecked ? '確認済' : '未確認'}
                          </Button>
                        );
                      })()}
                    </TableCell>
                  )}
                  {actualVisibleColumns.payment && (
                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        cursor: onPaymentCheck ? 'pointer' : 'default',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.02)'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onPaymentCheck) onPaymentCheck(row);
                      }}
                    >
                      {(() => {
                        const contactId = row.contact_code || detail.contact_id;
                        const invoices = squareDataCache[contactId] ?? [];
                        const sumPrice = row.total_amount || detail.sum_price;

                        // Square invoiceから金額を計算（PAIDのものを合算）
                        const paidInvoices = invoices.filter(inv => inv.invoice_status === 'PAID');
                        const totalPaidAmount = paidInvoices.reduce((sum, inv) => {
                          // total_money.amount は cents なので 100 で割る
                          const amount = inv.total_money?.amount || 0;
                          return sum + Math.round(amount / 100);
                        }, 0);

                        // ステータス判定
                        const hasPaid = invoices.some(inv => inv.invoice_status === 'PAID');
                        const hasUnpaid = invoices.some(inv => inv.invoice_status === 'UNPAID' || inv.invoice_status === 'DRAFT');
                        const hasPartialPaid = invoices.some(inv => inv.invoice_status === 'PARTIALLY_PAID');

                        let statusText = '';
                        let statusColor = '#757575';

                        const isManualPaid = Number(detail.payment) === 1;

                        if (squareDataLoading) {
                          statusText = '...';
                        } else if (hasPaid && !hasUnpaid && !hasPartialPaid) {
                          statusText = 'SQ済';
                          statusColor = '#4caf50';
                        } else if (isManualPaid) {
                          statusText = '済';
                          statusColor = '#4caf50';
                        } else if (hasPartialPaid) {
                          statusText = '部分';
                          statusColor = '#2196f3';
                        } else if (hasUnpaid) {
                          statusText = '未払';
                          statusColor = '#ff9800';
                        }


                        return (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            {/* 上段: Square金額 + ステータス */}
                            {(invoices.length > 0 || statusText) ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {totalPaidAmount > 0 ? totalPaidAmount.toLocaleString() : '-'}
                                </Typography>
                                {statusText && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: statusColor,
                                      fontWeight: 'bold',
                                      fontSize: '0.65rem',
                                    }}
                                  >
                                    {statusText}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {squareDataLoading ? '...' : '-'}
                              </Typography>
                            )}
                            {/* 下段: sum_price（予約金額） */}
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.7rem',
                              }}
                            >
                              ({sumPrice ? Number(sumPrice).toLocaleString() : '-'})
                            </Typography>
                          </Box>
                        );
                      })()}
                    </TableCell>
                  )}
                  {actualVisibleColumns.contact && (
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {(() => {
                        const tools = Array.isArray(detail.communication_tools)
                          ? detail.communication_tools
                          : [];

                        // 優先順位定数
                        const PRIORITY = ['LINE', 'WhatsApp', 'SMS', 'TEL', 'Instagram', 'その他'];

                        // 選択されたツールをラベルに変換し、優先順位でソート
                        const selectedTools = tools
                          .map(id => COMMUNICATION_TOOLS.find(t => t.id === id))
                          .filter(Boolean)
                          .sort((a, b) => PRIORITY.indexOf(a.label) - PRIORITY.indexOf(b.label));

                        const topTool = selectedTools[0];
                        const hasOthers = selectedTools.length > 1;
                        const colors = topTool ? (COMMUNICATION_MEDIA_COLORS[topTool.label] || COMMUNICATION_MEDIA_COLORS.default) : null;

                        return (
                          <Button
                            variant={topTool ? "contained" : "outlined"}
                            size="small"
                            onClick={() => {
                              setContactModalReservation(row);
                              setSelectedTools(tools);
                              setContactModalOpen(true);
                            }}
                            sx={{
                              fontSize: '0.65rem',
                              padding: '2px 6px',
                              minWidth: 'auto',
                              boxShadow: 'none',
                              ...(topTool ? {
                                backgroundColor: colors.bg_color,
                                color: colors.text_color,
                                borderColor: colors.bg_color,
                                '&:hover': {
                                  backgroundColor: colors.bg_color,
                                  filter: 'brightness(0.9)',
                                  borderColor: colors.bg_color,
                                }
                              } : {
                                borderColor: '#bdbdbd',
                                color: '#424242',
                                '&:hover': {
                                  backgroundColor: '#f5f5f5',
                                  borderColor: '#9e9e9e'
                                }
                              })
                            }}
                          >
                            {topTool ? `${topTool.label}${hasOthers ? ', 他' : ''}` : '未設定'}
                          </Button>
                        );
                      })()}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {filteredData.length === 0 && (
              <TableRow>
                <TableCell colSpan={Object.values(actualVisibleColumns).filter(Boolean).length} align="center">
                  No reservations found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <ReservationEditorPanel
        open={Boolean(selectedReservation)}
        editing={editingReservation}
        onCancel={() => setSelectedReservation(null)}
        onChange={(newFields) => setEditingReservation(prev => ({ ...prev, ...newFields }))}
        onSave={async (_data: any) => {
          if (onReservationSave && editingReservation) {
            try {
              await onReservationSave(editingReservation);
              setSelectedReservation(null);
            } catch (e) {
              console.error('Failed to save reservation:', e);
            }
          }
        }}
        disabled={false}
        registerNameInputRef={() => {}}
        reservationStatusList={statusOptions}
        vehicles={vehicles}
        vehicleTypes={vehicleTypes}
        onAssignClick={onAssignClick}
      />

      {/* 連絡手段選択モーダル */}
      <Dialog
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          連絡手段を選択
          <Typography variant="body2" color="text.secondary">
            {contactModalReservation?.customer_name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 1 }}>
            {COMMUNICATION_TOOLS.map((tool) => {
              const colors = COMMUNICATION_MEDIA_COLORS[tool.label] || COMMUNICATION_MEDIA_COLORS.default;
              const isSelected = selectedTools.includes(tool.id);
              return (
                <Chip
                  key={tool.id}
                  label={tool.label}
                  onClick={() => {
                    setSelectedTools(prev => {
                      if (prev.includes(tool.id)) {
                        return prev.filter(id => id !== tool.id);
                      } else {
                        return [...prev, tool.id].sort((a, b) => a - b);
                      }
                    });
                  }}
                  sx={{
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? colors.bg_color : 'transparent',
                    color: isSelected ? colors.text_color : 'inherit',
                    borderColor: isSelected ? colors.bg_color : '#bdbdbd',
                    '&:hover': {
                      opacity: 0.8,
                      backgroundColor: isSelected ? colors.bg_color : '#f5f5f5',
                    },
                    border: isSelected ? 'none' : '1px solid #bdbdbd',
                  }}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactModalOpen(false)} color="inherit">
            キャンセル
          </Button>
          <Button
            onClick={async () => {
              if (onCommunicationToolsUpdate && contactModalReservation) {
                await onCommunicationToolsUpdate(contactModalReservation, selectedTools, type);
              }
              setContactModalOpen(false);
            }}
            variant="contained"
            color="primary"
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 車両チェック項目モーダル */}
      <Dialog
        open={checkModalOpen}
        onClose={() => setCheckModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          車両チェック
          <Typography variant="body2" color="text.secondary">
            {checkModalVehicle?.vehicle_code}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
            {checkModalItems.map((item) => (
              <Button
                key={item.id}
                variant={item.value ? 'contained' : 'outlined'}
                onClick={() => {
                  setCheckModalItems(prev =>
                    prev.map(i => i.id === item.id ? { ...i, value: !i.value } : i)
                  );
                }}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  padding: '8px 16px',
                  backgroundColor: item.value ? (item.bg_color || '#e8f5e9') : 'transparent',
                  borderColor: item.value ? (item.bg_color || '#4caf50') : '#bdbdbd',
                  color: item.value ? (item.text_color || '#2e7d32') : '#757575',
                  fontWeight: item.value ? 'bold' : 'normal',
                  '&:hover': {
                    backgroundColor: item.value
                      ? (item.bg_color ? `${item.bg_color}dd` : '#c8e6c9')
                      : '#f5f5f5',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Checkbox
                    checked={!!item.value}
                    size="small"
                    sx={{
                      color: item.value ? 'inherit' : '#bdbdbd',
                      '&.Mui-checked': {
                        color: 'inherit',
                      },
                      padding: 0,
                      pointerEvents: 'none'
                    }}
                  />
                  <span style={{ flex: 1, textAlign: 'left' }}>{item.name}</span>
                  {item.require_flg && (
                    <Chip
                      label="必須"
                      size="small"
                      sx={{
                        height: '18px',
                        fontSize: '0.65rem',
                        backgroundColor: '#fff3e0',
                        color: '#e65100',
                      }}
                    />
                  )}
                </Box>
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckModalOpen(false)} color="inherit">
            キャンセル
          </Button>
          <Button
            onClick={async () => {
              if (checkModalVehicle && onCheckUpdate) {
                await onCheckUpdate(checkModalVehicle, checkModalItems);
              }
              setCheckModalOpen(false);
            }}
            variant="contained"
            color="primary"
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* Short Memo Edit Modal */}
      <Dialog
        open={shortMemoModal.open}
        onClose={() => setShortMemoModal({ ...shortMemoModal, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>直前メモ編集</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="メモ内容"
            fullWidth
            multiline
            minRows={3}
            value={shortMemoModal.value}
            onChange={(e) => setShortMemoModal({ ...shortMemoModal, value: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShortMemoModal({ ...shortMemoModal, open: false })}>キャンセル</Button>
          <Button
            onClick={() => {
              if (onQuickUpdate && shortMemoModal.row) {
                onQuickUpdate(shortMemoModal.row, { short_memo: shortMemoModal.value });
              }
              setShortMemoModal({ ...shortMemoModal, open: false });
            }}
            variant="contained"
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Time Edit Modal */}
      <Dialog
        open={requestTimeModal.open}
        onClose={() => setRequestTimeModal({ ...requestTimeModal, open: false })}
        maxWidth="xs"
      >
        <DialogTitle>希望時間編集</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={requestTimeModal.field === 'request_coming_time' ? '来店希望時間' : '返却希望時間'}
            type="time"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }} // 5 min step
            value={requestTimeModal.value}
            onChange={(e) => setRequestTimeModal({ ...requestTimeModal, value: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestTimeModal({ ...requestTimeModal, open: false })}>キャンセル</Button>
          <Button
            onClick={() => {
              if (onQuickUpdate && requestTimeModal.row) {
                onQuickUpdate(requestTimeModal.row, { [requestTimeModal.field]: requestTimeModal.value });
              }
              setRequestTimeModal({ ...requestTimeModal, open: false });
            }}
            variant="contained"
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 予約ステータス変更ダイアログ */}
      <Dialog open={resStatusModal.open} onClose={() => setResStatusModal({ open: false, row: null })}>
        <DialogTitle>予約ステータス変更</DialogTitle>
        <DialogContent>
          <Grid container spacing={1} sx={{ mt: 1 }}>
            {statusOptions.map((status) => (
              <Grid item xs={6} key={status.id} {...{} as any}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleResStatusUpdate(status.id)}
                  sx={{
                    py: 1.5,
                    backgroundColor: status.bg_color || '#eee',
                    color: status.text_color || '#000',
                    '&:hover': {
                      backgroundColor: status.bg_color || '#eee',
                      filter: 'brightness(0.9)',
                    }
                  }}
                >
                  {status.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResStatusModal({ open: false, row: null })}>キャンセル</Button>
        </DialogActions>
      </Dialog>

    </Paper >
  );
}

